import { useMutation, useQuery } from '@tanstack/react-query';
import type { CurrentUser, LoginRequest, MeResponse } from '@kominiarz/shared';
import { api, setCsrfToken } from '../lib/api';
import { clearOfflineCopy, offlineCopyUserId } from '../lib/offline';
import { forgetPushOnDevice } from '../lib/push';
import { ME_KEY, queryClient } from '../lib/queryClient';

function remember(data: MeResponse): MeResponse {
  setCsrfToken(data.csrfToken);
  return data;
}

export function useMe() {
  return useQuery({
    queryKey: ME_KEY,
    queryFn: async () => remember(await api<MeResponse>('/auth/me')),
    staleTime: 5 * 60_000,
  });
}

/** The signed-in user; only use below <RequireAuth>. */
export function useCurrentUser(): CurrentUser {
  const { data } = useMe();
  if (!data?.user) throw new Error('useCurrentUser used outside of an authenticated route');
  return data.user;
}

export function setCurrentUser(user: CurrentUser) {
  queryClient.setQueryData<MeResponse>(ME_KEY, (old) => ({ user, csrfToken: old?.csrfToken ?? null }));
}

export function useLogin() {
  return useMutation({
    mutationFn: (body: LoginRequest) => api<MeResponse>('/auth/login', { method: 'POST', body }),
    onSuccess: async (data) => {
      // Another person on this device must not see the previous user's offline copy.
      const owner = offlineCopyUserId();
      if (owner !== null && owner !== data.user?.id) await clearOfflineCopy();
      queryClient.clear();
      queryClient.setQueryData(ME_KEY, remember(data));
    },
  });
}

export function useLogout() {
  return useMutation({
    mutationFn: async () => {
      // Nothing of this user stays on the device: notifications off, offline copy and photos removed.
      await forgetPushOnDevice();
      try {
        return await api<{ ok: true }>('/auth/logout', { method: 'POST' });
      } finally {
        await clearOfflineCopy();
      }
    },
    onSettled: () => {
      setCsrfToken(null);
      queryClient.clear();
      queryClient.setQueryData<MeResponse>(ME_KEY, { user: null, csrfToken: null });
    },
  });
}
